"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import dynamic from "next/dynamic"
import { m } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Phone, MapPin, Clock, CheckCircle, Truck, Shield, Users, ArrowRight } from "lucide-react"

// Lazy-load non-critical components
const ModernProductCard = dynamic(() => import("@/components/modern-product-card"))
const ModernCategoryCard = dynamic(() => import("@/components/modern-category-card"))
const MobileProductCard = dynamic(() => import("@/components/mobile/mobile-product-card"))
const MobileCategoryCard = dynamic(() => import("@/components/mobile/mobile-category-card"))
const FloatingCard = dynamic(() => import("@/components/animated/floating-card"))
const TextReveal = dynamic(() => import("@/components/animated/text-reveal"))
const GradientBackground = dynamic(() => import("@/components/animated/gradient-background"))

import HeroWithVideo from "@/components/hero-with-video"
import { products } from "@/data/products"
import { categories } from "@/data/categories"
import { businessInfo } from "@/lib/metadata"
import { resources } from "@/data/resources"

export default function ClientHomePage() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  const allProducts = Array.isArray(products) ? products : []
  const allCategories = Array.isArray(categories) ? categories : []

  const featuredProducts = allProducts.slice(0, 8)
  const featuredCategories = allCategories.slice(0, 6)

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Schema Markup for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Real Metro Metal",
            "url": "https://realmetrometal.com",
            "logo": "https://realmetrometal.com/logo.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": businessInfo.address.streetAddress,
              "addressLocality": businessInfo.address.addressLocality,
              "addressCountry": "Sri Lanka",
            },
            "telephone": businessInfo.telephone,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://realmetrometal.com",
              },
            ],
          }),
        }}
      />

      {/* Hero Section with Video */}
      <HeroWithVideo />

      {/* Features Section */}
      <section className="py-20 relative">
        <GradientBackground />
        <div className="container mx-auto px-4 relative z-10">
          <TextReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Why Choose Real Metro Metal?</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                We are committed to providing the highest quality hardware products with exceptional service
              </p>
            </div>
          </TextReveal>

          <div className={`grid gap-8 ${isMobile ? "grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"}`}>
            {[
              {
                icon: CheckCircle,
                title: "Quality Assured",
                desc: isMobile
                  ? "International standards"
                  : "All our products meet international quality standards and specifications",
              },
              {
                icon: Truck,
                title: "Fast Delivery",
                desc: isMobile
                  ? "Quick & reliable"
                  : "Quick and reliable delivery across Colombo and surrounding areas",
              },
              {
                icon: Shield,
                title: "Trusted Supplier",
                desc: isMobile
                  ? "Years of experience"
                  : "Years of experience serving industrial and construction clients",
              },
              {
                icon: Users,
                title: "Expert Support",
                desc: isMobile
                  ? "Professional guidance"
                  : "Professional guidance to help you choose the right products",
              },
            ].map((feature, index) => (
              <FloatingCard key={feature.title} delay={index * 0.1}>
                <Card
                  className={`text-center bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full ${isMobile ? "rounded-2xl" : ""}`}
                >
                  <CardContent className={isMobile ? "p-4" : "p-8"}>
                    <m.div
                      className={`bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ${isMobile ? "w-12 h-12" : "w-16 h-16"}`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <feature.icon className={isMobile ? "h-6 w-6" : "h-8 w-8"} />
                    </m.div>
                    <h3 className={`font-semibold mb-3 ${isMobile ? "text-sm" : "text-lg"}`}>{feature.title}</h3>
                    <p className={`text-slate-600 leading-relaxed ${isMobile ? "text-xs" : "text-sm"}`}>
                      {feature.desc}
                    </p>
                  </CardContent>
                </Card>
              </FloatingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-20 bg-slate-50/50 relative">
        <div className="container mx-auto px-4">
          <TextReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Product Categories</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Explore our comprehensive range of industrial hardware products
              </p>
            </div>
          </TextReveal>

          {featuredCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredCategories.map((category, index) =>
                isMobile ? (
                  <MobileCategoryCard key={category.id} category={category} index={index} />
                ) : (
                  <ModernCategoryCard key={category.id} category={category} index={index} />
                ),
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-600">No categories available at the moment.</p>
            </div>
          )}

          <m.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Button
              size="lg"
              className={`bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600 shadow-lg px-8 py-4 ${isMobile ? "rounded-xl" : ""}`}
              asChild
            >
              <Link href="/categories">
                View All Categories
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </m.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 relative">
        <GradientBackground />
        <div className="container mx-auto px-4 relative z-10">
          <TextReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Featured Products</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">Discover our most popular hardware products</p>
            </div>
          </TextReveal>

          {featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product, index) => {
                // Ensure category is a string for type compatibility
                const safeProduct = {
                  ...product,
                  category: product.category === null ? "" : product.category,
                };
                return isMobile ? (
                  <MobileProductCard key={product.id} product={safeProduct} index={index} />
                ) : (
                  <ModernProductCard key={product.id} product={safeProduct} index={index} />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-600">No products available at the moment.</p>
            </div>
          )}

          <m.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Button
              size="lg"
              className={`bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg px-8 py-4 ${isMobile ? "rounded-xl" : ""}`}
              asChild
            >
              <Link href="/products">
                View All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </m.div>
        </div>
      </section>

      {/* Knowledge Hub / Resources Preview */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <Badge className="bg-blue-600/10 text-blue-600 border-blue-600/20 mb-4">Knowledge Hub</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-display">Expert Hardware Guides</h2>
              <p className="text-xl text-slate-600">
                In-depth technical resources to help you choose the right industrial hardware for your success.
              </p>
            </div>
            <Button variant="outline" size="lg" className="rounded-full group" asChild>
              <Link href="/resources">
                View All Guides
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources.slice(0, 3).map((resource, index) => (
              <m.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <CardContent className="p-8">
                    <Badge variant="secondary" className="mb-4 bg-slate-100 text-slate-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      {resource.category}
                    </Badge>
                    <h3 className="text-xl font-bold mb-4 line-clamp-2 min-h-[3.5rem] group-hover:text-blue-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-slate-600 mb-6 line-clamp-3 text-sm leading-relaxed">
                      {resource.excerpt}
                    </p>
                    <Link href={`/resources/${resource.slug}`} className="text-blue-600 font-semibold flex items-center text-sm group-hover:underline">
                      Read In-Depth
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <GradientBackground />
        <div className="container mx-auto  md:px-20 px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <m.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Get Started?</h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Contact us today for competitive quotes and expert advice on your hardware needs.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Phone, text: businessInfo.telephone },
                  {
                    icon: MapPin,
                    text: isMobile
                      ? `${businessInfo.address.addressLocality}, Sri Lanka`
                      : `${businessInfo.address.streetAddress}, ${businessInfo.address.addressLocality} ${businessInfo.address.postalCode}, Sri Lanka`,
                  },
                  { icon: Clock, text: "Mon - Sat: 8:00 AM - 6:00 PM" },
                ].map((item, index) => (
                  <m.div
                    key={index}
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <item.icon className="h-5 w-5 text-slate-400" />
                    <span className={isMobile ? "text-sm" : ""}>{item.text}</span>
                  </m.div>
                ))}
              </div>
            </m.div>

            <m.div
              className="text-center lg:text-right"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <m.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  className={`bg-gradient-to-r from-white to-slate-100 text-slate-900 hover:from-slate-100 hover:to-white shadow-2xl px-8 py-4 text-lg ${isMobile ? "rounded-xl w-full" : ""}`}
                  asChild
                >
                  <Link href="/contact">
                    Get Quote Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </m.div>
            </m.div>
          </div>
        </div>
      </section>

      {/* Mobile bottom spacing */}
      {isMobile && <div className="h-20" />}
    </div>
  )
}

